import { createFileRoute, redirect } from '@tanstack/react-router';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ClipboardList,
  Plus,
  Search,
  Download,
  Edit,
  Trash2,
  AlertCircle,
  Loader2,
  RefreshCw,
  ArrowRightCircle,
  Clock,
  CheckCircle,
  Send,
} from 'lucide-react';
import { useState } from 'react';
import { useInvoices, useDeleteInvoice, useConvertQuoteToInvoice } from '@/hooks/queries/useInvoices';
import { InvoiceForm } from '@/components/invoices/InvoiceForm';
import { toast } from '@/components/ui/toast/useToast';
import type { Invoice } from '@lebusiness/types';

export const Route = createFileRoute('/apps/quotes/')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }
  },
  component: QuotesPage,
});

function QuotesPage() {
  // Filter for quotes only
  const { data: quotes = [], isLoading, error, refetch } = useInvoices({ type: 'quote' });
  const deleteInvoice = useDeleteInvoice();
  const convertToInvoice = useConvertQuoteToInvoice();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [convertConfirmId, setConvertConfirmId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState<Invoice | null>(null);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat('fr-DZ', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);
  };

  // Filter only quotes from the results
  const allQuotes = quotes.filter((inv: any) => inv.type === 'quote');

  const filteredQuotes = allQuotes.filter((quote: any) => {
    const searchLower = searchQuery.toLowerCase();

    const matchesNumber = (quote.invoice_number || quote.invoiceNumber || '').toLowerCase().includes(searchLower);
    const matchesClient = quote.clients?.name?.toLowerCase().includes(searchLower);
    const matchesAmount = quote.total?.toString().includes(searchQuery.replace(/[^0-9]/g, ''));

    const matchesSearch = matchesNumber || matchesClient || matchesAmount;
    const matchesStatus = statusFilter === 'all' || quote.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: allQuotes.length,
    draft: allQuotes.filter((q: any) => q.status === 'draft').length,
    sent: allQuotes.filter((q: any) => q.status === 'sent').length,
    converted: allQuotes.filter((q: any) => q.status === 'paid').length, // "paid" = converted
    cancelled: allQuotes.filter((q: any) => q.status === 'cancelled').length,
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { label: string; color: string }> = {
      draft: { label: 'مسودة / Brouillon', color: 'bg-gray-100 text-gray-700' },
      sent: { label: 'مرسل / Envoyé', color: 'bg-blue-100 text-blue-700' },
      paid: { label: 'تم التحويل / Converti', color: 'bg-emerald-100 text-emerald-700' },
      partial: { label: 'قيد المراجعة / En cours', color: 'bg-yellow-100 text-yellow-700' },
      overdue: { label: 'منتهي / Expiré', color: 'bg-red-100 text-red-700' },
      cancelled: { label: 'مرفوض / Rejeté', color: 'bg-red-100 text-red-700' },
    };
    return badges[status] || badges.draft;
  };

  const handleNew = () => {
    setSelectedQuote(null);
    setFormOpen(true);
  };

  const handleEdit = (quote: any) => {
    setSelectedQuote(quote);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setSelectedQuote(null);
  };

  const handleDelete = async (id: string) => {
    if (deleteConfirmId === id) {
      try {
        await deleteInvoice.mutateAsync(id);
        toast.success(
          'تم الحذف بنجاح / Supprimé avec succès',
          'تم حذف عرض السعر / Le devis a été supprimé'
        );
        setDeleteConfirmId(null);
      } catch (error) {
        console.error('Failed to delete quote:', error);
        toast.error('خطأ / Erreur', 'فشل حذف عرض السعر / Échec de la suppression');
      }
    } else {
      setDeleteConfirmId(id);
      setTimeout(() => setDeleteConfirmId(null), 3000);
    }
  };

  const handleConvert = async (id: string) => {
    if (convertConfirmId === id) {
      try {
        await convertToInvoice.mutateAsync(id);
        toast.success(
          'تم التحويل بنجاح / Converti avec succès',
          'تم تحويل عرض السعر إلى فاتورة / Le devis a été converti en facture'
        );
        setConvertConfirmId(null);
      } catch (error) {
        console.error('Failed to convert quote:', error);
        toast.error(
          'خطأ / Erreur',
          error instanceof Error ? error.message : 'فشل تحويل عرض السعر / Échec de la conversion'
        );
      }
    } else {
      setConvertConfirmId(id);
      setTimeout(() => setConvertConfirmId(null), 5000);
    }
  };

  if (error) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              خطأ في تحميل البيانات / Erreur de chargement
            </h3>
            <p className="text-gray-600">
              {error instanceof Error ? error.message : 'Une erreur est survenue'}
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              عروض الأسعار / Devis
            </h1>
            <p className="mt-2 text-gray-600">
              إدارة عروض الأسعار والتحويل إلى فواتير / Gérez vos devis et convertissez-les en factures
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              تصدير / Exporter
            </Button>
            <Button
              size="sm"
              onClick={handleNew}
              className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              عرض سعر جديد / Nouveau devis
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">المجموع / Total</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <div className="p-3 bg-violet-50 rounded-lg">
                  <ClipboardList className="w-6 h-6 text-violet-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">مرسل / Envoyés</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.sent}</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg">
                  <Send className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">تم التحويل / Convertis</p>
                  <p className="text-2xl font-bold text-emerald-600">{stats.converted}</p>
                </div>
                <div className="p-3 bg-emerald-50 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">مسودة / Brouillons</p>
                  <p className="text-2xl font-bold text-gray-500">{stats.draft}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <Clock className="w-6 h-6 text-gray-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  placeholder="بحث برقم العرض، العميل أو المبلغ... / Rechercher par numéro, client ou montant..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              <div className="flex gap-2">
                {['all', 'draft', 'sent', 'paid', 'cancelled'].map((status) => (
                  <Button
                    key={status}
                    variant={statusFilter === status ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setStatusFilter(status)}
                    className={
                      statusFilter === status ? 'bg-violet-600 hover:bg-violet-700' : ''
                    }
                  >
                    {status === 'all' ? 'الكل / Tous' :
                     status === 'paid' ? 'محول / Converti' :
                     getStatusBadge(status).label.split(' / ')[1]}
                  </Button>
                ))}
                <Button variant="outline" size="sm" onClick={() => refetch()} className="ml-2">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quotes Table */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="border-b">
            <CardTitle>
              قائمة عروض الأسعار / Liste des devis ({filteredQuotes.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        الرقم / Numéro
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        التاريخ / Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        العميل / Client
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        المبلغ / Montant
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        العناصر / Articles
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        الحالة / Statut
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        إجراءات / Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredQuotes.map((quote: any) => {
                      const statusBadge = getStatusBadge(quote.status);
                      const isConverted = quote.status === 'paid';
                      const itemCount = quote.invoice_items?.length || 0;

                      return (
                        <tr key={quote.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm font-medium text-gray-900">
                              {quote.invoice_number || quote.invoiceNumber}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-600">
                              {formatDate(quote.date)}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {quote.clients?.name || 'N/A'}
                              </p>
                              {quote.clients?.phone && (
                                <p className="text-xs text-gray-500">{quote.clients.phone}</p>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm font-semibold text-gray-900">
                              {formatCurrency(quote.total)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-600">
                              {itemCount} {itemCount === 1 ? 'عنصر / article' : 'عناصر / articles'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge.color}`}
                            >
                              {statusBadge.label.split(' / ')[1]}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-1">
                              {/* Convert to Invoice - only if not already converted */}
                              {!isConverted && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleConvert(quote.id)}
                                  disabled={convertToInvoice.isPending}
                                  title="تحويل إلى فاتورة / Convertir en facture"
                                  className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                >
                                  {convertConfirmId === quote.id ? (
                                    <span className="text-xs font-medium">تأكيد؟</span>
                                  ) : (
                                    <ArrowRightCircle className="w-4 h-4" />
                                  )}
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(quote)}
                                title="تعديل / Modifier"
                              >
                                <Edit className="w-4 h-4 text-blue-600" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(quote.id)}
                                disabled={deleteInvoice.isPending}
                              >
                                {deleteConfirmId === quote.id ? (
                                  <span className="text-xs text-red-600 font-medium">تأكيد؟</span>
                                ) : (
                                  <Trash2 className="w-4 h-4 text-red-600" />
                                )}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {!isLoading && filteredQuotes.length === 0 && (
              <div className="text-center py-12">
                <ClipboardList className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">
                  لا توجد عروض أسعار / Aucun devis trouvé
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {allQuotes.length === 0
                    ? 'ابدأ بإنشاء عرض سعر جديد / Commencez par créer un nouveau devis'
                    : 'جرب تغيير معايير البحث / Essayez de modifier vos critères'}
                </p>
                {allQuotes.length === 0 && (
                  <Button
                    className="mt-4 bg-gradient-to-r from-violet-500 to-purple-600"
                    onClick={handleNew}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    إنشاء أول عرض سعر / Créer votre premier devis
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Invoice Form Modal - pre-set to quote type */}
      <InvoiceForm
        open={formOpen}
        onOpenChange={handleFormClose}
        invoice={selectedQuote ? selectedQuote : { type: 'quote' } as any}
        onSuccess={() => {
          handleFormClose();
        }}
      />
    </DashboardLayout>
  );
}
